import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PageHeader from '@/shared/components/PageHeader';
import {
  Alert, Button, Card, Descriptions, Drawer, Image, Input, InputNumber, Select,
  Space, Table, Tabs, Tag, Typography,
} from '@/shared/ui/primitives';
import { CloudDownloadOutlined, EyeOutlined, LinkOutlined, ReloadOutlined, CheckOutlined, CloseOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import { UserSelect } from '@/shared/ui/entity-selects';
import { externalAdsApi, type ExternalAd } from '../api/externalAds.api';
import type { ColumnsType } from '@/shared/ui/subpath-types';

const STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'processing',
  NEEDS_EDIT: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const TYPE_COLORS: Record<string, string> = {
  ROOM_AD: 'blue',
  ROOM_WANTED: 'purple',
  UNKNOWN: 'default',
};

function localImagePath(image: Record<string, any>) {
  return image.localPath || image.local_path || '';
}

function imageUrlFor(ad: ExternalAd, image: Record<string, any>) {
  return externalAdsApi.imageUrl(ad.id, localImagePath(image));
}

export default function ExternalAdsReviewPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const pageSize = 20;
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ search: '', status: '', classificationType: '', city: '', keyword: '', minConfidence: undefined as number | undefined });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [adminNotes, setAdminNotes] = useState('');

  const listQuery = useQuery({
    queryKey: ['external-ads', filters, page],
    queryFn: () => externalAdsApi.list({ page, size: pageSize, ...filters }),
  });

  const detailQuery = useQuery({
    queryKey: ['external-ads', selectedId],
    queryFn: () => externalAdsApi.getById(selectedId!),
    enabled: selectedId != null,
  });

  const selected = detailQuery.data;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['external-ads'] });
  };

  const importMutation = useMutation({
    mutationFn: externalAdsApi.importHaraj,
    onSuccess: (res) => { toast.success(t('externalAds.toasts.imported', { inserted: res.inserted, updated: res.updated, filtered: res.filtered ?? 0 })); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.message || t('externalAds.toasts.importFailed')),
  });

  const saveMutation = useMutation({
    mutationFn: ({ id, body }: any) => externalAdsApi.update(id, body),
    onSuccess: (ad) => { toast.success(t('externalAds.toasts.saved')); setDraft(ad.mappedPayload || {}); setAdminNotes(ad.adminNotes || ''); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.message || t('externalAds.toasts.saveFailed')),
  });

  const actionMutation = useMutation({
    mutationFn: ({ action, id }: any) => {
      if (action === 'approve') return externalAdsApi.approve(id);
      if (action === 'reject') return externalAdsApi.reject(id, adminNotes);
      return externalAdsApi.reclassify(id);
    },
    onSuccess: (ad, vars: any) => {
      toast.success(vars.action === 'approve' ? t('externalAds.toasts.approved') : vars.action === 'reject' ? t('externalAds.toasts.rejected') : t('externalAds.toasts.reclassified'));
      setDraft(ad.mappedPayload || {});
      setAdminNotes(ad.adminNotes || '');
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('externalAds.toasts.actionFailed')),
  });

  const openDetails = (id: number) => {
    setSelectedId(id);
    externalAdsApi.getById(id).then((ad) => {
      setDraft(ad.mappedPayload || {});
      setAdminNotes(ad.adminNotes || '');
    });
  };

  const updateDraft = (key: string, value: any) => setDraft((prev) => ({ ...prev, [key]: value }));
  const records = listQuery.data?.content || [];
  const totalItems = listQuery.data?.totalItems || 0;
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  const canGoPrev = page > 0;
  const canGoNext = page + 1 < pageCount;

  const renderImagePreview = (ad: ExternalAd) => (
    <Space size="small" className="external-ads-thumbnail-row">
      {(ad.localImages || []).slice(0, 3).map((img, idx) => (
        <Image key={idx} src={imageUrlFor(ad, img)} width={36} height={36} style={{ objectFit: 'cover', borderRadius: 4 }} />
      ))}
      {(ad.localImages?.length || 0) > 3 && <Tag>+{ad.localImages.length - 3}</Tag>}
    </Space>
  );

  const columns: ColumnsType<ExternalAd> = [
    { title: t('common.id'), dataIndex: 'id', width: 80 },
    {
      title: t('externalAds.columns.ad'),
      width: 300,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700 }}>{r.rawTitle || t('externalAds.untitled')}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>#{r.sourceAdId} · {r.matchedKeyword || t('externalAds.noKeyword')}</div>
        </div>
      ),
    },
    { title: t('common.status'), dataIndex: 'status', width: 140, render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag> },
    { title: t('externalAds.columns.classification'), dataIndex: 'classificationType', width: 140, render: (v) => <Tag color={TYPE_COLORS[v] || 'default'}>{v}</Tag> },
    { title: t('externalAds.columns.confidence'), dataIndex: 'classificationConfidence', width: 120, render: (v) => <Tag>{Math.round((v || 0) * 100)}%</Tag> },
    { title: t('externalAds.columns.source'), dataIndex: 'source', width: 90, render: (v) => <Tag color="geekblue">{v}</Tag> },
    { title: t('externalAds.columns.location'), width: 160, render: (_, r) => [r.rawCity, r.rawDistrict].filter(Boolean).join(' / ') || '—' },
    {
      title: t('externalAds.columns.images'),
      width: 140,
      render: (_, r) => renderImagePreview(r),
    },
    { title: t('externalAds.columns.scraped'), dataIndex: 'scrapedAt', width: 160, render: (v) => formatDateTime(v) },
    {
      title: t('common.actions'),
      width: 120,
      render: (_, r) => (
        <Space>
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => openDetails(r.id)} />
          {r.sourceUrl && <Button type="text" size="small" icon={<LinkOutlined />} onClick={() => window.open(r.sourceUrl!, '_blank', 'noopener,noreferrer')} />}
        </Space>
      ),
    },
  ];

  const validationErrors = useMemo(() => (draft.validationErrors || []) as string[], [draft]);

  return (
    <div className="external-ads-page">
      <PageHeader
        title={t('externalAds.title')}
        subtitle={t('externalAds.subtitle')}
        action={<Button type="primary" icon={<CloudDownloadOutlined />} loading={importMutation.isPending} onClick={() => importMutation.mutate()}>{t('externalAds.importHaraj')}</Button>}
      />

      <Card style={{ marginBottom: 12 }} className="external-ads-filter-card">
        <div className="external-ads-filters">
          <Input.Search placeholder={t('externalAds.filters.search')} value={filters.search} onChange={(e: any) => setFilters((f) => ({ ...f, search: e.target.value }))} />
          <Select placeholder={t('common.status')} allowClear value={filters.status || undefined} onChange={(v: any) => setFilters((f) => ({ ...f, status: v || '' }))} options={['PENDING_REVIEW', 'NEEDS_EDIT', 'APPROVED', 'REJECTED'].map((v) => ({ value: v, label: v }))} />
          <Select placeholder={t('externalAds.columns.classification')} allowClear value={filters.classificationType || undefined} onChange={(v: any) => setFilters((f) => ({ ...f, classificationType: v || '' }))} options={['ROOM_AD', 'ROOM_WANTED', 'UNKNOWN'].map((v) => ({ value: v, label: v }))} />
          <Input placeholder={t('common.city')} value={filters.city} onChange={(e: any) => setFilters((f) => ({ ...f, city: e.target.value }))} />
          <Input placeholder={t('externalAds.keyword')} value={filters.keyword} onChange={(e: any) => setFilters((f) => ({ ...f, keyword: e.target.value }))} />
          <InputNumber placeholder={t('externalAds.filters.minConfidence')} min={0} max={1} step={0.05} value={filters.minConfidence} onChange={(v: any) => setFilters((f) => ({ ...f, minConfidence: v }))} />
          <Button className="external-ads-filter-action" onClick={() => setFilters({ search: '', status: '', classificationType: '', city: '', keyword: '', minConfidence: undefined })}>{t('common.reset')}</Button>
        </div>
      </Card>

      <div className="external-ads-table">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={records}
          loading={listQuery.isLoading}
          scroll={{ x: 1400 }}
          locale={{ emptyText: t('common.noData') }}
          pagination={{
            current: page + 1,
            pageSize,
            total: totalItems,
            onChange: (p) => setPage(p - 1),
            showSizeChanger: false,
            showTotal: (total, range) => t('common.showingRange', { from: range[0], to: range[1], total }),
          }}
        />
      </div>

      <div className="external-ads-mobile-list">
        {listQuery.isLoading && (
          <Card>
            <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
          </Card>
        )}
        {!listQuery.isLoading && records.length === 0 && (
          <Card>
            <Typography.Text type="secondary">{t('common.noData')}</Typography.Text>
          </Card>
        )}
        {!listQuery.isLoading && records.map((record) => (
          <Card key={record.id} className="external-ad-card">
            <div className="external-ad-card-top">
              <div>
                <div className="external-ad-card-title">{record.rawTitle || t('externalAds.untitled')}</div>
                <div className="external-ad-card-meta">#{record.sourceAdId} · {record.matchedKeyword || t('externalAds.noKeyword')}</div>
              </div>
              <Tag>{Math.round((record.classificationConfidence || 0) * 100)}%</Tag>
            </div>
            <div className="external-ad-card-badges">
              <Tag color={STATUS_COLORS[record.status] || 'default'}>{record.status}</Tag>
              <Tag color={TYPE_COLORS[record.classificationType] || 'default'}>{record.classificationType}</Tag>
              <Tag color="geekblue">{record.source}</Tag>
            </div>
            <div className="external-ad-card-row">
              <span>{t('externalAds.columns.location')}</span>
              <strong>{[record.rawCity, record.rawDistrict].filter(Boolean).join(' / ') || '—'}</strong>
            </div>
            <div className="external-ad-card-row">
              <span>{t('externalAds.columns.scraped')}</span>
              <strong>{formatDateTime(record.scrapedAt)}</strong>
            </div>
            {record.localImages?.length ? <div className="external-ad-card-images">{renderImagePreview(record)}</div> : null}
            <div className="external-ad-card-actions">
              <Button size="small" icon={<EyeOutlined />} onClick={() => openDetails(record.id)}>{t('common.view')}</Button>
              {record.sourceUrl && (
                <Button size="small" icon={<LinkOutlined />} onClick={() => window.open(record.sourceUrl!, '_blank', 'noopener,noreferrer')}>{t('externalAds.openSource')}</Button>
              )}
            </div>
          </Card>
        ))}
        {!listQuery.isLoading && totalItems > 0 && (
          <div className="external-ads-mobile-pagination">
            <Button size="small" disabled={!canGoPrev} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('common.previous')}</Button>
            <span>{page + 1} / {pageCount}</span>
            <Button size="small" disabled={!canGoNext} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>{t('common.next')}</Button>
          </div>
        )}
      </div>

      <Drawer open={selectedId != null} onClose={() => setSelectedId(null)} width={920} className="external-ads-drawer" title={selected?.rawTitle || t('externalAds.externalAd')}>
        {selected && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Space wrap className="external-ads-drawer-badges">
              <Tag color={STATUS_COLORS[selected.status]}>{selected.status}</Tag>
              <Tag color={TYPE_COLORS[selected.classificationType]}>{selected.classificationType}</Tag>
              <Tag>{Math.round((selected.classificationConfidence || 0) * 100)}%</Tag>
              <Tag color="geekblue">{selected.source}</Tag>
              {selected.sourceUrl && <Button size="small" icon={<LinkOutlined />} onClick={() => window.open(selected.sourceUrl!, '_blank', 'noopener,noreferrer')}>{t('externalAds.openSource')}</Button>}
            </Space>

            {validationErrors.length > 0 && (
              <Alert type="warning" showIcon message={t('externalAds.approvalBlocked')} description={validationErrors.join(', ')} />
            )}

            <Tabs
              items={[
                {
                  key: 'review',
                  label: t('externalAds.tabs.review'),
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label={t('externalAds.sourceId')}>{selected.sourceAdId}</Descriptions.Item>
                        <Descriptions.Item label={t('externalAds.keyword')}>{selected.matchedKeyword || '—'}</Descriptions.Item>
                        <Descriptions.Item label={t('common.city')}>{selected.rawCity || '—'}</Descriptions.Item>
                        <Descriptions.Item label={t('common.district')}>{selected.rawDistrict || '—'}</Descriptions.Item>
                        <Descriptions.Item label={t('externalAds.price')}>{selected.rawPrice || '—'}</Descriptions.Item>
                        <Descriptions.Item label={t('externalAds.relevance')}>{selected.relevanceScore ?? '—'}</Descriptions.Item>
                      </Descriptions>
                      <Typography.Title level={5}>{t('externalAds.rawDescription')}</Typography.Title>
                      <Typography.Paragraph>{selected.rawDescription || '—'}</Typography.Paragraph>
                      <Typography.Title level={5}>{t('externalAds.columns.images')}</Typography.Title>
                      <Space wrap className="external-ads-image-grid">
                        {(selected.localImages || []).map((img, idx) => (
                          <Image key={idx} src={imageUrlFor(selected, img)} width={120} height={90} style={{ objectFit: 'cover', borderRadius: 6 }} />
                        ))}
                        {!selected.localImages?.length && <Typography.Text type="secondary">{t('externalAds.noLocalImages')}</Typography.Text>}
                      </Space>
                    </Space>
                  ),
                },
                {
                  key: 'edit',
                  label: t('externalAds.tabs.mappedPayload'),
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div className="external-ads-edit-grid external-ads-edit-grid--compact">
                        <Select value={selected.classificationType} options={['ROOM_AD', 'ROOM_WANTED', 'UNKNOWN'].map((v) => ({ value: v, label: v }))} onChange={(v: any) => saveMutation.mutate({ id: selected.id, body: { classificationType: v, mappedPayload: draft, adminNotes } })} />
                        <Select value={selected.status} options={['PENDING_REVIEW', 'NEEDS_EDIT', 'REJECTED'].map((v) => ({ value: v, label: v }))} onChange={(v: any) => saveMutation.mutate({ id: selected.id, body: { status: v, mappedPayload: draft, adminNotes } })} />
                      </div>
                      <div data-allstak-mask data-sensitive>
                        <UserSelect value={draft.ownerUserId ?? undefined} onChange={(v: any) => updateDraft('ownerUserId', v)} placeholder={t('externalAds.ownerRequired')} />
                      </div>
                      <Input value={draft.title || ''} onChange={(e: any) => updateDraft('title', e.target.value)} placeholder={t('common.title')} />
                      <Input.TextArea rows={5} value={draft.description || ''} onChange={(e: any) => updateDraft('description', e.target.value)} placeholder={t('common.description')} data-allstak-mask="true" data-sensitive="true" />
                      <div className="external-ads-edit-grid">
                        <Input value={draft.city || ''} onChange={(e: any) => updateDraft('city', e.target.value)} placeholder={t('common.city')} />
                        <Input value={draft.district || ''} onChange={(e: any) => updateDraft('district', e.target.value)} placeholder={t('common.district')} />
                        <InputNumber value={draft.price ?? undefined} onChange={(v: any) => updateDraft('price', v)} placeholder={t('externalAds.priceBudget')} />
                        <Select value={draft.genderPreference || 'UNKNOWN'} options={['MALE', 'FEMALE', 'ANY', 'UNKNOWN'].map((v) => ({ value: v, label: v }))} onChange={(v: any) => updateDraft('genderPreference', v)} />
                        <Select value={draft.smokingPreference || 'UNKNOWN'} options={['YES', 'NO', 'UNKNOWN'].map((v) => ({ value: v, label: t('externalAds.smokingValue', { value: v }) }))} onChange={(v: any) => updateDraft('smokingPreference', v)} />
                      </div>
                      <Input.TextArea rows={3} value={adminNotes} onChange={(e: any) => setAdminNotes(e.target.value)} placeholder={t('externalAds.adminNotes')} data-allstak-mask="true" data-sensitive="true" />
                      <Space className="external-ads-action-row" wrap>
                        <Button type="primary" loading={saveMutation.isPending} onClick={() => saveMutation.mutate({ id: selected.id, body: { mappedPayload: draft, adminNotes } })}>{t('externalAds.saveEdits')}</Button>
                        <Button icon={<ReloadOutlined />} loading={actionMutation.isPending} onClick={() => actionMutation.mutate({ id: selected.id, action: 'reclassify' })}>{t('externalAds.reclassify')}</Button>
                        <Button danger icon={<CloseOutlined />} loading={actionMutation.isPending} onClick={() => actionMutation.mutate({ id: selected.id, action: 'reject' })}>{t('externalAds.reject')}</Button>
                        <Button type="primary" icon={<CheckOutlined />} loading={actionMutation.isPending} onClick={() => actionMutation.mutate({ id: selected.id, action: 'approve' })}>{t('externalAds.approveToDraft')}</Button>
                      </Space>
                    </Space>
                  ),
                },
                {
                  key: 'json',
                  label: t('externalAds.tabs.json'),
                  children: <pre className="external-ads-json">{JSON.stringify({ classificationReason: selected.classificationReason, mappedPayload: draft, rawImages: selected.rawImages, localImages: selected.localImages }, null, 2)}</pre>,
                },
              ]}
            />
          </Space>
        )}
      </Drawer>
    </div>
  );
}
